import React, { Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTwitter, FaLinkedinIn, FaWhatsapp, FaArrowRight, FaTimes, FaExpand } from 'react-icons/fa';
import AppUrl from '../../restApi/AppUrl';
import IRestClient from '../../restApi/IRestClient';
import { Link } from 'react-router-dom';
import './Team.css';
import SectionHeader from '../SectionHeader/SectionHeader';

const gridVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } }
};

const cardVariants = {
  hidden: { opacity: 0, y: 38, scale: 0.97 },
  visible: {
    opacity: 1, y: 0, scale: 1,
    transition: { duration: 0.52, ease: [0.22, 1, 0.36, 1] }
  }
};


class Team extends Component {
  constructor() {
    super();
    this.state = {
      myData: [],
      loaded: false,
      selectedMember: null,
      isModalOpen: false,
      hoveredCard: null
    };
  }

  componentDidMount() {
    IRestClient.GetRequest(AppUrl.teamhome).then(result => {
      this.setState({ myData: result || [], loaded: true });
    }).catch(() => {
      this.setState({ loaded: true });
    });
  }

  componentWillUnmount() {
    document.body.style.overflow = '';
  }

  openModal = (member) => {
    this.setState({ selectedMember: member, isModalOpen: true });
    document.body.style.overflow = 'hidden';
  };

  closeModal = () => {
    this.setState({ selectedMember: null, isModalOpen: false });
    document.body.style.overflow = 'unset';
  };

  render() {
    const { myData, loaded, isModalOpen, selectedMember, hoveredCard } = this.state;

    return (
      <>
        <section className="team-section">
          <div className="team-container">

            {/* Header */}
            <SectionHeader
              badge="Our People"
              title="The People Behind the Mission"
              subtitle="Meet the passionate individuals driving our humanitarian efforts forward every day."
              variant="light"
              align="center"
              className="mb-14"
            />

            {/* Grid */}
            <motion.div
              className="team-grid"
              variants={gridVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
            >
              {!loaded
                ? [1, 2, 3, 4].map(i => (
                    <div key={i} className="team-skeleton">
                      <div className="team-skeleton-img" />
                      <div className="team-skeleton-body">
                        <div className="team-skeleton-line" style={{ width: '68%' }} />
                        <div className="team-skeleton-line" style={{ width: '44%', marginTop: '0.5rem' }} />
                        <div className="team-skeleton-socials">
                          <div className="team-skeleton-dot" />
                          <div className="team-skeleton-dot" />
                          <div className="team-skeleton-dot" />
                        </div>
                      </div>
                    </div>
                  ))
                : myData.map((team, index) => {
                    const isHovered = hoveredCard === index;
                    const socials = [
                      team.twitter  && { href: team.twitter,  cls: 'team-social-twitter',  icon: <FaTwitter /> },
                      team.linkedin && { href: team.linkedin, cls: 'team-social-linkedin', icon: <FaLinkedinIn /> },
                      team.whatsapp && { href: `https://wa.me/${team.whatsapp.replace(/\D/g,'')}`, cls: 'team-social-whatsapp', icon: <FaWhatsapp /> },
                    ].filter(Boolean);

                    return (
                      <motion.div
                        key={index}
                        className="team-card"
                        variants={cardVariants}
                        onMouseEnter={() => this.setState({ hoveredCard: index })}
                        onMouseLeave={() => this.setState({ hoveredCard: null })}
                        whileHover={{ y: -8, boxShadow: '0 24px 48px rgba(30,58,138,0.18)', transition: { duration: 0.28 } }}
                      >
                        {/* Photo — click opens HD modal */}
                        <div className="team-photo" onClick={() => this.openModal(team)} style={{ cursor: 'pointer' }}>
                          <motion.img
                            src={team.team_image}
                            alt={team.team_name}
                            className="team-photo-img"
                            animate={{ scale: isHovered ? 1.07 : 1 }}
                            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                            loading="lazy"
                          />
                          {/* Hover: dim + centered expand icon only */}
                          <motion.div
                            style={{
                              position: 'absolute', inset: 0,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              background: 'rgba(0,0,0,0.22)',
                              pointerEvents: 'none',
                            }}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: isHovered ? 1 : 0 }}
                            transition={{ duration: 0.22 }}
                          >
                            <div style={{
                              width: '52px', height: '52px',
                              background: 'rgba(255,255,255,0.93)',
                              borderRadius: '50%',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              boxShadow: '0 4px 20px rgba(0,0,0,0.28)',
                            }}>
                              <FaExpand style={{ color: '#1d4ed8', fontSize: '17px' }} />
                            </div>
                          </motion.div>
                        </div>

                        {/* Info — always visible, no duplication */}
                        <div className="team-info">
                          <div className="team-info-accent" />
                          <h3 className="team-name">{team.team_name}</h3>
                          <p className="team-role">{team.team_position}</p>
                          {socials.length > 0 && (
                            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.875rem' }}>
                              {socials.map(({ href, cls, icon }, si) => (
                                <a key={si} href={href} target="_blank" rel="noopener noreferrer"
                                  className={`team-social-icon ${cls}`}>
                                  {icon}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    );
                  })
              }
            </motion.div>

            {/* CTA */}
            <motion.div
              className="team-cta"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <motion.div whileHover={{ scale: 1.04, y: -2 }} whileTap={{ scale: 0.97 }} style={{ display: 'inline-block' }}>
                <Link to="/allteam" className="team-cta-btn">
                  View Full Team <FaArrowRight />
                </Link>
              </motion.div>
            </motion.div>

          </div>
        </section>

        {/* Member Modal */}
        <AnimatePresence>
          {isModalOpen && selectedMember && (
            <motion.div
              className="team-modal-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={this.closeModal}
            >
              <button
                className="team-modal-close"
                onClick={e => { e.stopPropagation(); this.closeModal(); }}
                aria-label="Close"
              >
                <FaTimes />
              </button>

              <motion.div
                className="team-modal-card"
                initial={{ scale: 0.88, opacity: 0, y: 24 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.88, opacity: 0 }}
                transition={{ duration: 0.32, type: 'spring', stiffness: 320, damping: 28 }}
                onClick={e => e.stopPropagation()}
              >
                <div className="team-modal-photo">
                  <img
                    src={selectedMember.team_image}
                    alt={selectedMember.team_name}
                  />
                </div>

                <div className="team-modal-info">
                  <div className="team-modal-accent" />
                  <h3 className="team-modal-name">{selectedMember.team_name}</h3>
                  <p className="team-modal-role">{selectedMember.team_position}</p>
                  <div className="team-modal-socials">
                    {selectedMember.twitter && (
                      <a
                        href={selectedMember.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="team-social-icon team-social-twitter"
                      >
                        <FaTwitter />
                      </a>
                    )}
                    {selectedMember.linkedin && (
                      <a
                        href={selectedMember.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="team-social-icon team-social-linkedin"
                      >
                        <FaLinkedinIn />
                      </a>
                    )}
                    {selectedMember.whatsapp && (
                      <a
                        href={`https://wa.me/${selectedMember.whatsapp.replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="team-social-icon team-social-whatsapp"
                      >
                        <FaWhatsapp />
                      </a>
                    )}
                  </div>
                  <p className="team-modal-hint">Click outside to close</p>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  }
}

export default Team;
